// src/Services/feedbackService.js
// In-house replacement for features.vote/UserJot: posts + votes attach to whatever
// Firebase identity the player already has (anonymous or signed-in) via authService,
// exactly like every other collection in this app — no separate feedback-tool account.
//
// Author context (games played, account age, etc.) is captured once at post-creation
// time as a denormalized snapshot on the post document itself, per the product
// constraint against composite queries for the admin view.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";

export const STATUSES = ["open", "planned", "in_progress", "done"];

// The stored values stay the same (mobile and old posts use them); only the
// words players read changed.
export const STATUS_LABELS = {
  open: "Under review",
  planned: "Planned",
  in_progress: "Building",
  done: "Shipped",
};

/** Votes on a post, counting imported votes that have no voter list. */
export const voteCountOf = (post) => post.voteCount ?? post.votes?.length ?? 0;

/** Bug reports are only shown to whoever sent them (and the admin), and a
 *  post merged into another is shown as that one. */
export const isPublicIdea = (post) => post.category !== "bug" && !post.mergedInto;

export const CATEGORIES = ["feature", "improvement", "bug"];

export const CATEGORY_LABELS = {
  feature: "Feature idea",
  improvement: "Improvement",
  bug: "Bug",
};

const FEEDBACK_COLLECTION = "feedbackPosts";
const CHANGELOG_COLLECTION = "changelogEntries";

/** Builds the denormalized author snapshot from the currently signed-in user's own
 * profile doc — one read, no query, captured once at post time rather than joined
 * live against `users/{uid}` on every admin view render. */
async function buildAuthorSnapshot(uid) {
  const user = authService.auth.currentUser;
  const userSnap = await getDoc(doc(db, "users", uid));
  const userData = userSnap.exists() ? userSnap.data() : {};

  const createdAt = userData.createdAt?.toDate ? userData.createdAt.toDate() : null;
  const accountAgeDays = createdAt
    ? Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)))
    : null;

  return {
    displayName: userData.displayName || (user?.isAnonymous ? "Guest player" : user?.email) || "Player",
    isAnonymous: user?.isAnonymous ?? true,
    totalGamesPlayed: userData.stats?.totalGamesPlayed || 0,
    accountAgeDays,
  };
}

const feedbackService = {
  async listPosts() {
    const snap = await getDocs(query(collection(db, FEEDBACK_COLLECTION), orderBy("createdAt", "desc")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async createPost({ title, description, category }) {
    const uid = await authService.getCurrentUserId();
    const authorSnapshot = await buildAuthorSnapshot(uid);

    const post = {
      title: title.trim(),
      description: description.trim(),
      category,
      status: "open",
      votes: [uid],
      voteCount: 1,
      authorId: uid,
      authorSnapshot,
      migrated: false,
      createdAt: serverTimestamp(),
    };

    const ref = await addDoc(collection(db, FEEDBACK_COLLECTION), post);
    return { id: ref.id, ...post };
  },

  /** Toggles the current user's vote on a post. Returns the new voted state. */
  async toggleVote(postId, currentVotes) {
    const uid = await authService.getCurrentUserId();
    const hasVoted = currentVotes.includes(uid);
    const postRef = doc(db, FEEDBACK_COLLECTION, postId);

    await updateDoc(postRef, {
      votes: hasVoted ? arrayRemove(uid) : arrayUnion(uid),
      voteCount: increment(hasVoted ? -1 : 1),
    });

    return !hasVoted;
  },

  /** Admin-only in practice (gated in the admin UI, src/Screens/admin/AdminApp.jsx). */
  async setStatus(postId, status) {
    await updateDoc(doc(db, FEEDBACK_COLLECTION, postId), { status });
  },

  /** Admin fix for a post filed under the wrong type. */
  async setCategory(postId, category) {
    await updateDoc(doc(db, FEEDBACK_COLLECTION, postId), { category });
  },

  /** The team's public answer on a post; empty text removes it. */
  async setReply(postId, text) {
    const trimmed = (text || "").trim();
    await updateDoc(doc(db, FEEDBACK_COLLECTION, postId), {
      teamReply: trimmed ? { text: trimmed, at: new Date() } : null,
    });
  },

  /**
   * Folds a duplicate into another post: the voters join the target's (each
   * counted once) and the duplicate stops showing, pointing at the target.
   * Imported posts have a vote count but no voter list, so those extra votes
   * carry over as a number.
   */
  async mergePost(source, target) {
    const unlisted = (p) => Math.max(0, (p.voteCount ?? 0) - (p.votes?.length ?? 0));
    const targetRef = doc(db, FEEDBACK_COLLECTION, target.id);
    const sourceRef = doc(db, FEEDBACK_COLLECTION, source.id);
    // One transaction on fresh copies: votes cast since the page loaded are
    // kept, and the duplicate is hidden only if its votes really moved.
    return runTransaction(db, async (tx) => {
      const [t, src] = await Promise.all([tx.get(targetRef), tx.get(sourceRef)]);
      if (!t.exists() || !src.exists()) throw new Error("One of these posts no longer exists.");
      const tData = t.data();
      const sData = src.data();
      if (sData.mergedInto) throw new Error("That post was already merged.");
      const votes = [...new Set([...(tData.votes || []), ...(sData.votes || [])])];
      const voteCount = votes.length + unlisted(tData) + unlisted(sData);
      tx.update(targetRef, { votes, voteCount });
      tx.update(sourceRef, { mergedInto: target.id });
      return { votes, voteCount };
    });
  },

  async deletePost(postId) {
    await deleteDoc(doc(db, FEEDBACK_COLLECTION, postId));
  },

  async listChangelog() {
    const snap = await getDocs(query(collection(db, CHANGELOG_COLLECTION), orderBy("date", "desc")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async createChangelogEntry({ title, description, date, postId }) {
    const entry = {
      title: title.trim(),
      description: description.trim(),
      date: date ? new Date(date) : new Date(),
      // The idea this shipped, so the update can link back to it.
      postId: postId || null,
      createdAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, CHANGELOG_COLLECTION), entry);
    return { id: ref.id, ...entry };
  },

  async deleteChangelogEntry(entryId) {
    await deleteDoc(doc(db, CHANGELOG_COLLECTION, entryId));
  },
};

export default feedbackService;
